import { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import useToastStore, {
  type ToastItemT,
  type ToastOptionT,
} from '@/stores/pure/ToastStore';

import { Flex } from '../base';
import Portal from '../Portal/Portal';

const ToastManager = () => {
  const { toasts } = useToastStore((state) => ({ toasts: state.toasts }));
  const [showChild, setShowChild] = useState(false);

  useEffect(() => {
    setShowChild(true);
  }, []);

  if (!showChild) return null;
  return (
    <Portal id="toast-layer">
      <Style g="8px" d="column-reverse" position="center">
        {toasts.map((toast) => (
          <ToastItem key={toast.toastId} {...toast} />
        ))}
      </Style>
    </Portal>
  );
};

const Style = styled(Flex)<ToastOptionT>`
  position: fixed;
  transition: all 0.2s;
  ${({ position }) => {
    switch (position) {
      case 'center':
        return css`
          top: 24px;
          left: 50%;
          transform: translateX(-50%);
        `;
      case 'right':
        return css`
          top: 24px;
          right: 24px;
        `;
      case 'left':
        return css`
          top: 24px;
          left: 0px;
        `;
      case 'bottom-right':
        return css`
          right: 24px;
          bottom: 24px;
        `;
      default:
        return css`
          left: 50%;
          transform: translateX(-50%);
        `;
    }
  }}
`;

type ToastItemProps = ToastItemT;

const ToastItem = (props: ToastItemProps) => {
  const popToast = useToastStore((state) => state.popToast);

  useEffect(() => {
    setTimeout(() => {
      popToast(props.toastId);
    }, props.duration);
  }, []);

  return (
    <ToastItemStyle {...props} state={props.closeTrigger ? 'leave' : 'enter'} />
  );
};

const ToastItemStyle = styled(Flex)<{ state: 'enter' | 'leave' }>`
  animation: ${({ state }) =>
      state === 'enter' ? ToastEnterAnimation : ToastLeaveAnimation}
    300ms forwards;

  white-space: pre-wrap;
  word-break: break-word;

  align-items: flex-start;
`;

const ToastEnterAnimation = keyframes`
  0% {
    transform: translateY(30px);
    opacity: 0;
  }
  100% {
    transform: translateY(0px);
    opacity: 1;
  }
`;

const ToastLeaveAnimation = keyframes`
  0% {
    transform: translateY(0px);
    opacity: 1;
  }
  100% {
    transform: translateY(30px);
    opacity: 0;
  }
`;

export default ToastManager;
